<script lang="ts">
  import ModalShell from './ModalShell.svelte'
  import ConfirmDialog from './ConfirmDialog.svelte'
  import { settings } from '../state/settings.svelte'
  import { clearAllLocalData } from '../lib/clearData'
  import { showToast } from '../state/toast.svelte'

  interface Props {
    onClose: () => void
  }
  let { onClose }: Props = $props()

  let confirmClearAll = $state(false)

  /*
   * Four preset stops. Values are intentionally a wide spread so the
   * differences are obvious in the live sample below. Each stop is what
   * .prose's `font-size` is set to on the actual preview pane — the prose
   * type scale (h1/h2/p/etc.) is scoped to use em-based tokens so every
   * heading and paragraph scales together.
   */
  const ZOOM_STOPS = [
    { label: 'Small', value: 85 },
    { label: 'Default', value: 100 },
    { label: 'Large', value: 120 },
    { label: 'XL', value: 140 },
  ] as const

  let zoomPct = $derived(settings.previewZoom)

  async function clearData(): Promise<void> {
    await clearAllLocalData()
    showToast('Local data cleared')
    onClose()
    location.reload()
  }
</script>

<ModalShell variant="drawer" labelledBy="settings-title" onClose={onClose}>
  <div class="panel">
    <header>
      <h2 id="settings-title">Settings</h2>
      <button type="button" class="close" onclick={onClose} aria-label="Close settings">✕</button>
    </header>

    <section>
      <div class="row">
        <h3>Text size</h3>
        <span class="zoom-value">{zoomPct}%</span>
      </div>
      <p class="hint">Scales every heading, paragraph, and code block in the preview.</p>

      <div class="stops" role="radiogroup" aria-label="Preview text size">
        {#each ZOOM_STOPS as stop}
          <button
            type="button"
            role="radio"
            aria-checked={settings.previewZoom === stop.value}
            class:active={settings.previewZoom === stop.value}
            onclick={() => (settings.previewZoom = stop.value)}
          >{stop.label}</button>
        {/each}
      </div>

      <input
        type="range"
        min="60"
        max="180"
        step="5"
        bind:value={settings.previewZoom}
        aria-label="Custom text size"
      />

      <!--
        Live sample — gives users a within-drawer preview of what the picker
        does. Uses the same .prose scoped em tokens so it scales identically
        to the real preview pane.
      -->
      <div class="sample" style="font-size: {zoomPct / 100}rem;" aria-hidden="true">
        <h4>Preview sample</h4>
        <p>The quick brown fox jumps over the lazy dog. <code>inline()</code> and <strong>bold</strong> are sized to match.</p>
      </div>
    </section>

    <section class="danger-section">
      <button type="button" class="danger" onclick={() => (confirmClearAll = true)}>Clear all local data</button>
      <p class="hint">Removes your draft, settings, and recents from this browser.</p>
    </section>
  </div>
</ModalShell>

{#if confirmClearAll}
  <ConfirmDialog
    title="Clear all local data?"
    message="Your current draft, settings, and recent documents will be removed from this browser. This cannot be undone."
    confirmLabel="Clear everything"
    danger
    onConfirm={() => { void clearData() }}
    onClose={() => (confirmClearAll = false)}
  />
{/if}

<style>
  .panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: var(--space-4) var(--space-5);
    overflow-y: auto;
    box-sizing: border-box;
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-5);
    flex-shrink: 0;
  }
  h2 {
    margin: 0;
    font-size: var(--text-xl);
    font-weight: 600;
  }
  h3 {
    margin: 0;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
  }
  .row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: var(--space-1);
  }
  .zoom-value {
    font-size: var(--text-sm);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--accent);
  }
  section {
    margin-bottom: var(--space-5);
    padding-bottom: var(--space-5);
    border-bottom: 1px solid var(--border-subtle);
  }
  section:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  .close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    font-size: var(--text-lg);
    line-height: 1;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .close:hover {
    background: var(--bg-sunken);
    color: var(--text-primary);
  }
  .hint {
    margin: 0 0 var(--space-3);
    font-size: var(--text-sm);
    color: var(--text-muted);
    line-height: 1.4;
  }
  .stops {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-1);
    margin-bottom: var(--space-3);
  }
  .stops button {
    padding: 8px 4px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    background: var(--bg-surface);
    color: var(--text-secondary);
    font: inherit;
    font-size: var(--text-xs);
    font-weight: 500;
    cursor: pointer;
  }
  .stops button:hover {
    border-color: var(--border-strong);
    color: var(--text-primary);
  }
  .stops button.active {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--accent);
  }
  input[type='range'] {
    width: 100%;
    margin: 0 0 var(--space-3);
    accent-color: var(--accent);
  }
  .sample {
    padding: var(--space-3) var(--space-4);
    background: var(--bg-page);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    line-height: 1.55;
    /* Local em scale matches Preview.svelte's .prose tokens. */
    --text-base: 1em;
    --text-sm: 0.875em;
    --text-lg: 1.125em;
  }
  .sample h4 {
    margin: 0 0 var(--space-2);
    font-size: var(--text-lg);
    font-weight: 600;
  }
  .sample p {
    margin: 0;
    font-size: var(--text-base);
  }
  .sample code {
    background: var(--code-bg);
    color: var(--code-text);
    font-family: var(--font-mono);
    font-size: 0.9em;
    padding: 0.1em 0.35em;
    border-radius: var(--radius-sm);
  }
  .sample strong {
    font-weight: 600;
  }
  .danger-section {
    margin-top: auto;
    padding-top: var(--space-5);
  }
  .danger {
    width: 100%;
    padding: 10px;
    border: 1px solid var(--callout-caution);
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--callout-caution);
    font: inherit;
    font-weight: 500;
    cursor: pointer;
  }
  .danger:hover {
    background: color-mix(in srgb, var(--callout-caution) 12%, transparent);
  }
</style>
